import PropTypes from 'prop-types';
import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import i18n from 'i18n-js';
import { buildUrl } from 'utils';
import Layout from 'components/Layout';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Wizard from 'components/UI/Wizard';
import { uiActions } from 'state/modules/UI';
import Button from '@mui/material/Button';
import { SkusActions } from 'state/modules/skus';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { withRouter, useParams, useLocation } from 'react-router-dom';
import { SkuAccounting, SkuBasicInfo, SkuBehaviour } from 'components/Skus/SkusWizard';
// TODO replace by StyledStepperContainer
import useStyles from './SkusOnboarding.styled';
// import { styled } from '@mui/material/styles';
// const StepperContainer = styled(Box)({
//   margin: '0 auto',
//   maxWidth: 800,
// });

const SkusOnboarding = ({ history }) => {
  SkusOnboarding.propTypes = {
    history: PropTypes.shape({
      push: PropTypes.func.isRequired,
    }).isRequired,
  };
  const { id } = useParams();
  const classes = useStyles();
  const [showSaveAndExitBtn, setShowSaveAndExitBtn] = useState(false);
  // Eliminated useStyles, replaced with Emotion styled
  const query = new URLSearchParams(useLocation().search);
  const step = query?.get('step');
  const [showSaveAndExitModal, setShowSaveAndExitModal] = useState(false);
  const successMessage = `Sku ${id ? 'updated' : 'created'} successfully`;

  const stepsList = [
    {
      position: 1,
      key: 'accounting',
      label: i18n.t('skus.accounting'),
    },
    {
      position: 2,
      key: 'details',
      label: i18n.t('skus.basic_info'),
    },
    {
      position: 3,
      key: 'behaviour',
      label: i18n.t('skus.behaviour'),
    },
  ];

  const {
    loading,
    fetchedCreateSku,
    sku,
    onboarding: { exitStep, position, exitOnboarding, errors, steps, lastStep },
  } = useSelector((state) => state.skus);

  const formRef = useRef(null);

  const dispatch = useDispatch();

  /**
   * Set title of the page
   */
  const setTitle = (sku) => {
    if (id && sku) {
      return `${i18n.t('skus.sku_id')} #${sku?.id}`;
    } else if (id) {
      return i18n.t('skus.update_sku_info');
    }
    return i18n.t('skus.create_sku');
  };

  const onPrevious = () => {
    dispatch(SkusActions.changePosition('prev'));
  };

  const handleNextPosition = () => {
    dispatch(SkusActions.changePosition('next'));
  };

  const handleEndOnboarding = (showFeedback = true) => {
    dispatch(SkusActions.resetOnboarding());
    if (showFeedback) {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: successMessage,
        })
      );
    }
    history.push(buildUrl('/skus'));
  };

  const handleNextStep = (exit = false) => {
    switch (position) {
      case 0:
        if (exit) {
          handleEndOnboarding(exit);
        } else {
          formRef.current.submitForm();
        }
        break;
      case 1:
        if (exit) {
          handleEndOnboarding(exit);
        } else {
          formRef.current.submitForm();
        }
        break;
      case 2:
        if (formRef?.current) {
          return formRef.current.submitForm();
        }
        break;
      default:
        break;
    }
  };

  const onSaveAndExit = () => {
    handleNextStep(true);
  };

  const handleExit = (exit) => {
    dispatch(SkusActions.setExit(exit || !exitStep, 'exitStep'));
  };

  const handleShowOnSaveAndExitModal = () => {
    setShowSaveAndExitModal((prevState) => !prevState);
  };

  const handleOnSaveAndExit = () => {
    handleExit(true);
    setShowSaveAndExitModal((prevState) => !prevState);
  };

  useEffect(() => {
    if (id) {
      dispatch(SkusActions.fetchSkuById(id, 'onboarding'));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (fetchedCreateSku) {
      if (sku?.id) {
        history.push(buildUrl(`/skus/${sku?.id}`));
      } else {
        history.push(buildUrl('/skus'));
      }
    }
  }, [fetchedCreateSku, history, sku]);

  useEffect(() => {
    setShowSaveAndExitBtn(false);
  }, [position]);

  useEffect(() => {
    if (step) {
      dispatch(SkusActions.changeStep(step));
    }
  }, [step, dispatch]);

  useEffect(() => {
    if (errors && lastStep && (Array.isArray(errors) || Object.keys(errors).length !== 0)) {
      handleEndOnboarding();
    } else if (lastStep) {
      displaySuccessModal();
    }
  }, [lastStep]); // eslint-disable-line

  useEffect(() => {
    if (exitOnboarding) {
      handleEndOnboarding();
    }
  }, [exitOnboarding]); // eslint-disable-line

  const displaySuccessModal = () => {
    handleEndOnboarding();
  };

  const skuWizard = () => (
    <Wizard
      mode="create"
      exit={exitStep}
      steps={stepsList}
      loading={loading}
      title={setTitle(steps?.details)}
      position={position}
      handleExit={handleExit}
      onNext={handleNextStep}
      onPrevious={onPrevious}
      onSaveAndExit={onSaveAndExit}
      customStepperClass={classes.stepperContainer}
      successMessage={successMessage}
      handleEndOnboarding={handleEndOnboarding}
      showSaveAndExitModal={showSaveAndExitModal}
      handleShowOnSaveAndExitModal={handleShowOnSaveAndExitModal}
    >
      {position === 0 && (
        <SkuAccounting
          exit={exitStep}
          errors={errors}
          formRef={formRef}
          step={steps.accounting}
          onSave={setShowSaveAndExitBtn}
          handleNextPosition={handleNextPosition}
        />
      )}
      {position === 1 && (
        <SkuBasicInfo
          exit={exitStep}
          errors={errors}
          formRef={formRef}
          step={steps.details}
          accounting={steps.accounting}
          onSave={setShowSaveAndExitBtn}
          handleNextPosition={handleNextPosition}
        />
      )}
      {position === 2 && (
        <SkuBehaviour
          exit={exitStep}
          errors={errors}
          formRef={formRef}
          step={steps.details}
          onSave={setShowSaveAndExitBtn}
          handleNextPosition={handleNextPosition}
        />
      )}
    </Wizard>
  );

  return (
    <Layout title="Dashboard">
      <Container maxWidth="lg">
        <Grid container direction="row" justifyContent="center" alignItems="center">
          <Grid size={{ xs: 12 }}>
            <Grid
              container
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 6 }}
            >
              <Grid>
                <Typography variant="h6" gutterBottom>
                  <Box sx={{ fontWeight: 'bold', fontSize: 24, my: 1 }}>{setTitle()}</Box>
                </Typography>
              </Grid>
              {showSaveAndExitBtn && (
                <Grid>
                  <Button onClick={handleOnSaveAndExit} variant="contained" color="primary">
                    {i18n.t('wizard.save_and_exit')}
                  </Button>
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid size={{ xs: 12 }}>{skuWizard()}</Grid>
        </Grid>
      </Container>
    </Layout>
  );
};

export default withRouter(SkusOnboarding);
